/**
 * Login Page - Modern fullscreen background design
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { seedAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Sun, Moon, Loader2, ArrowRight, Eye, EyeOff } from 'lucide-react';
import DEFAULT_LOGOS from '../../lib/logos';

import './styles.css';

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const seedData = async () => {
      try {
        setSeeding(true);
        await seedAPI.seed();
      } catch (err) {
        // Ignore if already seeded
      } finally {
        setSeeding(false);
      }
    };
    seedData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success(t('auth.loginSuccess'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const fillDemoCredentials = (type) => {
    const credentials = {
      admin: { email: 'admin@nuponto.com.br', password: 'admin123' },
      master: { email: 'master@restopos.com', password: 'admin123' },
      waiter: { email: 'garcom@nuponto.com.br', password: 'admin123' },
    };
    setEmail(credentials[type].email);
    setPassword(credentials[type].password);
  };

  return (
    <div className="login-page" data-testid="login-page">
      {/* Full background image */}
      <div className="login-bg" />
      <div className="login-bg-overlay" />

      {/* Theme toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="login-theme-toggle"
        onClick={toggleTheme}
        data-testid="theme-toggle"
      >
        {theme === 'dark' ? <Sun className="h-5 w-5 text-white" /> : <Moon className="h-5 w-5 text-white" />}
      </Button>

      {/* Content */}
      <div className="login-content">
        {/* Left - Hero */}
        <div className="login-hero">
          <img 
            src={DEFAULT_LOGOS.logoWhite} 
            alt="SisPDV" 
            className="login-hero-logo"
            data-testid="login-hero-logo"
          />
          <h1 className="login-hero-title">
            Gestão completa,
            <br />
            <span>resultados reais.</span>
          </h1>
          <p className="login-hero-subtitle">
            {t('auth.heroText')}
          </p>
          <div className="login-features">
            <div className="login-feature-pill">PDV Completo</div>
            <div className="login-feature-pill">Gestão de Estoque</div>
            <div className="login-feature-pill">Relatórios</div>
            <div className="login-feature-pill">Multi-Caixa</div>
          </div>
        </div>

        {/* Right - Login Card */}
        <div className="login-card-wrapper">
          <div className="login-card" data-testid="login-card">
            <div className="login-card-header">
              <img 
                src={DEFAULT_LOGOS.iconLight} 
                alt="SisPDV" 
                className="login-card-logo-img"
                data-testid="login-card-logo"
              />
              <h2>SisPDV</h2>
              <p>{t('auth.loginDescription')}</p>
            </div>
            
            <form onSubmit={handleSubmit} className="login-form">
              <div className="login-field">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="login-input"
                  data-testid="login-email"
                />
              </div>
              <div className="login-field">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <div className="login-password-wrapper">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="login-input"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="login-submit-btn" 
                disabled={loading || seeding}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('auth.loggingIn')}
                  </>
                ) : seeding ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('auth.preparing')}
                  </>
                ) : (
                  <>
                    {t('auth.login')}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
